import { useState, useCallback, useEffect } from "react";
import { useTheme } from "../context/Themecontext";
import Api from "../api/Api";

export default function DashboardPage() {
  const { dark } = useTheme();

  // Totais
  const [stats, setStats] = useState({
    total_formacao: 0,
    total_conteudo: 0,
    total_departamento: 0,
    total_usuario: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);

  // Rankings
  const [rankings, setRankings] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(true);

  // Linha expandida + detalhe
  const [expandedId, setExpandedId] = useState(null);
  const [detailType, setDetailType] = useState("usuario"); // "usuario" | "departamento"
  const [detailData, setDetailData] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // ══════════ Tokens de cor (inalterados) ══════════
  const t = dark
    ? {
        bg: "#080c14",
        bgCard: "#111827",
        bgHover: "rgba(0,144,231,0.08)",
        bgActive: "rgba(0,144,231,0.16)",
        bgInput: "#1a2232",
        border: "rgba(0,174,255,0.12)",
        borderActive: "rgba(0,174,255,0.35)",
        textPrimary: "#eef5ff",
        textSecondary: "#b0c7e0",
        textMuted: "#7b93b0",
        textActive: "#00b4ff",
        shadow: "0 12px 40px rgba(0,0,0,0.8)",
        rowHover: "rgba(0,144,231,0.06)",
        success: "#22c55e",
        successBg: "rgba(34,197,94,0.12)",
        warning: "#f59e0b",
        warningBg: "rgba(245,158,11,0.12)",
        danger: "#ef4444",
        dangerBg: "rgba(239,68,68,0.12)",
      }
    : {
        bg: "#f4f7fc",
        bgCard: "#ffffff",
        bgHover: "#f0f6ff",
        bgActive: "#e6f0ff",
        bgInput: "#fafbfd",
        border: "#e2e8f0",
        borderActive: "rgba(0,100,230,0.4)",
        textPrimary: "#0f1a2e",
        textSecondary: "#475569",
        textMuted: "#8b9eb0",
        textActive: "#0057ff",
        shadow: "0 8px 30px rgba(0,50,150,0.06)",
        rowHover: "#f8faff",
        success: "#16a34a",
        successBg: "#f0fdf4",
        warning: "#d97706",
        warningBg: "#fffbeb",
        danger: "#ef4444",
        dangerBg: "#fef2f2",
      };

  // ── Carregar totais ────────────────────────────────
  const loadStats = useCallback(async () => {
    try {
      const res = await Api.getDashboard();
      const data = res?.data || res;
      if (data) {
        setStats({
          total_formacao: data.total_formacao || 0,
          total_conteudo: data.total_conteudo || 0,
          total_departamento: data.total_departamento || 0,
          total_usuario: data.total_usuario || 0,
        });
      }
    } catch {
      setErrorMsg("Erro ao carregar dados do dashboard.");
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // ── Carregar ranking das formações ────────────────
  const loadRankings = useCallback(async () => {
    try {
      const res = await Api.rankingFormacao();
      const data = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setRankings(data);
    } catch {
      setErrorMsg("Erro ao carregar ranking de formações.");
    } finally {
      setLoadingRankings(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadRankings();
  }, [loadStats, loadRankings]);

  // ── Carregar detalhe (usuários ou departamentos) ──
  const loadDetail = useCallback(async (idFormacao, type) => {
    setLoadingDetail(true);
    try {
      let res;
      if (type === "usuario") {
        res = await Api.rankingUsuario({ id: idFormacao });
      } else {
        res = await Api.rankingDepartamento({ id: idFormacao });
      }
      const data = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setDetailData(data);
    } catch {
      setDetailData([]);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  // Alterna a linha expandida
  const toggleRow = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    setDetailType("usuario");
    loadDetail(id, "usuario");
  };

  // Troca o menu (usuário / departamento) dentro da linha aberta
  const switchDetailType = (type) => {
    setDetailType(type);
    if (expandedId) loadDetail(expandedId, type);
  };

  // ── Helpers ──────────────────────────────────────
  const formatPercent = (valor) => {
    const num = parseFloat(valor);
    if (isNaN(num)) return "0%";
    return num.toFixed(0) + "%";
  };

  const badgeTone = (pct) => {
    const num = parseFloat(pct);
    if (num >= 70) return "green";
    if (num >= 40) return "yellow";
    return "red";
  };

  // ═══════════════ Estilos ═══════════════
  const styles = `
    .dash-page { font-family: 'Inter', system-ui, sans-serif; background: ${t.bg}; min-height: 100vh; padding: 40px 24px; }
    .dash-container { max-width: 1180px; margin: 0 auto; }

    .dash-header { margin-bottom: 36px; display: flex; align-items: baseline; justify-content: space-between; }
    .dash-heading { font-size: 1.5rem; font-weight: 700; letter-spacing: -0.03em; color: ${t.textPrimary}; }
    .dash-sub { font-size: 0.84rem; color: ${t.textMuted}; margin-top: 4px; }

    .dash-error {
      background: ${t.dangerBg}; color: ${t.danger};
      padding: 12px 18px; border-radius: 10px; margin-bottom: 24px;
      font-size: 0.85rem; font-weight: 500;
    }

    /* Cards de totais — limpos, sem ícones */
    .dash-cards {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 1px;
      background: ${t.border};
      border: 1px solid ${t.border};
      border-radius: 14px;
      overflow: hidden;
      margin-bottom: 40px;
    }
    .dash-card {
      background: ${t.bgCard};
      padding: 22px 24px;
    }
    .dash-card-label {
      font-size: 0.72rem; font-weight: 600; letter-spacing: 0.05em;
      text-transform: uppercase; color: ${t.textMuted}; margin-bottom: 10px;
    }
    .dash-card-value {
      font-size: 1.9rem; font-weight: 700; letter-spacing: -0.03em;
      color: ${t.textPrimary}; line-height: 1;
    }

    /* Seção */
    .dash-section-title {
      font-size: 0.95rem; font-weight: 700; color: ${t.textPrimary};
      margin-bottom: 14px;
    }

    /* Tabela de ranking */
    .dash-table-card {
      background: ${t.bgCard}; border: 1px solid ${t.border};
      border-radius: 14px; overflow: hidden;
    }
    .dash-table { width: 100%; border-collapse: collapse; }
    .dash-table thead th {
      text-align: left; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.06em;
      text-transform: uppercase; color: ${t.textMuted}; padding: 13px 20px;
      background: ${t.bgHover}; border-bottom: 1px solid ${t.border};
    }
    .dash-table thead th.col-toggle { width: 40px; padding: 13px 0 13px 20px; }
    .dash-table tbody td { padding: 13px 20px; font-size: 0.86rem; color: ${t.textPrimary}; border-bottom: 1px solid ${t.border}; }
    .dash-table tbody tr.data-row { cursor: pointer; transition: background 0.15s; }
    .dash-table tbody tr.data-row:hover { background: ${t.rowHover}; }
    .dash-table tbody tr.data-row.is-open { background: ${t.bgHover}; }
    .dash-table tbody tr:last-child td { border-bottom: none; }

    .dash-toggle-cell { padding: 13px 0 13px 20px; }
    .dash-toggle-btn {
      width: 22px; height: 22px; border: none; background: none; padding: 0;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; color: ${t.textMuted}; transition: transform 0.18s ease, color 0.15s;
    }
    .dash-toggle-btn.is-open { color: ${t.textActive}; transform: rotate(90deg); }
    .dash-toggle-btn svg { width: 14px; height: 14px; }

    .dash-badge {
      display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 6px;
      font-size: 0.72rem; font-weight: 600;
    }
    .dash-badge.green { background: ${t.successBg}; color: ${t.success}; }
    .dash-badge.red { background: ${t.dangerBg}; color: ${t.danger}; }
    .dash-badge.yellow { background: ${t.warningBg}; color: ${t.warning}; }

    /* Painel expandido, dentro da linha */
    .dash-expand-row td {
      padding: 0; border-bottom: 1px solid ${t.border};
      background: ${t.bg === "#080c14" ? "rgba(0,0,0,0.15)" : "#fafbfe"};
    }
    .dash-expand-inner { padding: 4px 20px 20px 52px; }

    .dash-tabs { display: flex; gap: 4px; padding: 14px 0 12px; border-bottom: 1px solid ${t.border}; margin-bottom: 0; }
    .dash-tab {
      border: none; background: none; padding: 6px 4px; margin-right: 18px;
      font-size: 0.8rem; font-weight: 600; color: ${t.textMuted};
      cursor: pointer; position: relative; transition: color 0.15s;
    }
    .dash-tab.active { color: ${t.textActive}; }
    .dash-tab.active::after {
      content: ""; position: absolute; left: 0; right: 0; bottom: -1px;
      height: 2px; background: ${t.textActive}; border-radius: 2px;
    }

    .dash-subtable { width: 100%; border-collapse: collapse; margin-top: 6px; }
    .dash-subtable th {
      text-align: left; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.05em;
      text-transform: uppercase; color: ${t.textMuted}; padding: 10px 12px;
    }
    .dash-subtable td { padding: 9px 12px; font-size: 0.82rem; color: ${t.textSecondary}; border-top: 1px solid ${t.border}; }
    .dash-subtable tr:first-child td { border-top: none; }

    .dash-loading, .dash-empty { text-align: center; padding: 36px; color: ${t.textMuted}; font-size: 0.85rem; }
    .dash-mini-loading { padding: 18px 0; color: ${t.textMuted}; font-size: 0.82rem; }
  `;

  const ChevronIcon = ({ open }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );

  return (
    <div className="dash-page">
      <style>{styles}</style>
      <div className="dash-container">
        <div className="dash-header">
          <div>
            <h1 className="dash-heading">Dashboard</h1>
            <p className="dash-sub">Visão geral do sistema</p>
          </div>
        </div>

        {errorMsg && <div className="dash-error">{errorMsg}</div>}

        {/* Cards de totais */}
        <div className="dash-cards">
          <div className="dash-card">
            <div className="dash-card-label">Formações</div>
            <div className="dash-card-value">{loadingStats ? "—" : stats.total_formacao}</div>
          </div>
          <div className="dash-card">
            <div className="dash-card-label">Conteúdos</div>
            <div className="dash-card-value">{loadingStats ? "—" : stats.total_conteudo}</div>
          </div>
          <div className="dash-card">
            <div className="dash-card-label">Departamentos</div>
            <div className="dash-card-value">{loadingStats ? "—" : stats.total_departamento}</div>
          </div>
          <div className="dash-card">
            <div className="dash-card-label">Utilizadores</div>
            <div className="dash-card-value">{loadingStats ? "—" : stats.total_usuario}</div>
          </div>
        </div>

        {/* Ranking de formações */}
        <div className="dash-section-title">Ranking de Formações</div>

        {loadingRankings ? (
          <div className="dash-table-card"><div className="dash-loading">Carregando ranking...</div></div>
        ) : rankings.length === 0 ? (
          <div className="dash-table-card"><div className="dash-empty">Nenhuma formação encontrada.</div></div>
        ) : (
          <div className="dash-table-card">
            <table className="dash-table">
              <thead>
                <tr>
                  <th className="col-toggle"></th>
                  <th>Formação</th>
                  <th>Respostas</th>
                  <th>Certas</th>
                  <th>Erradas</th>
                  <th>% Acerto</th>
                </tr>
              </thead>
              <tbody>
                {rankings.map((r) => {
                  const isOpen = expandedId === r.id;
                  return (
                    <>
                      <tr
                        key={r.id}
                        className={`data-row${isOpen ? " is-open" : ""}`}
                        onClick={() => toggleRow(r.id)}
                      >
                        <td className="dash-toggle-cell">
                          <button
                            type="button"
                            className={`dash-toggle-btn${isOpen ? " is-open" : ""}`}
                            aria-label={isOpen ? "Fechar detalhes" : "Ver detalhes"}
                            onClick={(e) => { e.stopPropagation(); toggleRow(r.id); }}
                          >
                            <ChevronIcon open={isOpen} />
                          </button>
                        </td>
                        <td><span style={{ fontWeight: 600 }}>{r.formacao || "—"}</span></td>
                        <td>{r.total_respostas || 0}</td>
                        <td><span className="dash-badge green">{r.respostas_certas || 0}</span></td>
                        <td><span className="dash-badge red">{r.respostas_erradas || 0}</span></td>
                        <td>
                          <span className={`dash-badge ${badgeTone(r.percentual_acerto)}`}>
                            {formatPercent(r.percentual_acerto)}
                          </span>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="dash-expand-row" key={`${r.id}-detail`}>
                          <td colSpan={6}>
                            <div className="dash-expand-inner">
                              <div className="dash-tabs">
                                <button
                                  className={`dash-tab${detailType === "usuario" ? " active" : ""}`}
                                  onClick={() => switchDetailType("usuario")}
                                >
                                  Por Utilizador
                                </button>
                                <button
                                  className={`dash-tab${detailType === "departamento" ? " active" : ""}`}
                                  onClick={() => switchDetailType("departamento")}
                                >
                                  Por Departamento
                                </button>
                              </div>

                              {loadingDetail ? (
                                <div className="dash-mini-loading">Carregando...</div>
                              ) : detailData.length === 0 ? (
                                <div className="dash-mini-loading">Nenhum dado disponível.</div>
                              ) : (
                                <table className="dash-subtable">
                                  <thead>
                                    <tr>
                                      <th>{detailType === "usuario" ? "Utilizador" : "Departamento"}</th>
                                      <th>Respostas</th>
                                      <th>Certas</th>
                                      <th>Erradas</th>
                                      <th>% Acerto</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detailData.map((item) => (
                                      <tr key={item.id}>
                                        <td style={{ fontWeight: 600, color: t.textPrimary }}>
                                          {detailType === "usuario" ? item.nome : item.departamento}
                                        </td>
                                        <td>{item.total_respostas || 0}</td>
                                        <td><span className="dash-badge green">{item.respostas_certas || 0}</span></td>
                                        <td><span className="dash-badge red">{item.respostas_erradas || 0}</span></td>
                                        <td>
                                          <span className={`dash-badge ${badgeTone(item.percentual_acerto)}`}>
                                            {formatPercent(item.percentual_acerto)}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}